<template>
  <div class="dashboard" :class="{ 'has-tabbar': isMobile, 'wizard-only-no-nav': showWizardOnlyNoNav }" :style="wizardFooterStyle">
    <!-- Gradient Background -->
    <div class="bg-gradient"></div>
    <div class="bg-blob blob-1"></div>
    <div class="bg-blob blob-2"></div>
    <div class="bg-blob blob-3"></div>

    <!-- Mobile header removed: app header below is used for all (desktop + mobile) -->

    <!-- App header (desktop: always; mobile: when wizard-only to match wizard flow) -->
    <div class="app-header-wrap" :class="{ 'with-progress': showWizardOnlyNoNav }">
      <header class="app-header">
        <router-link to="/" class="app-header-logo">
          <AppLogo class="app-header-logo-img" />
        </router-link>
        <div class="app-header-right" ref="headerUserCardWrap">
          <button
            type="button"
            class="app-header-user-card"
            aria-haspopup="true"
            :aria-expanded="headerDropdownOpen"
            @click="headerDropdownOpen = !headerDropdownOpen"
          >
            <div class="user-details">
              <span class="user-name">{{ userDisplayName }}</span>
              <span class="user-credits">
                <i class="fa fa-bolt"></i>
                {{ userCredits }} credits
              </span>
            </div>
          </button>
          <div v-show="headerDropdownOpen" class="wizard-header-dropdown app-header-dropdown" role="menu">
            <router-link
              v-if="isAdmin"
              to="/admin/analytics"
              role="menuitem"
              class="wizard-header-dropdown-item"
              @click="headerDropdownOpen = false"
            >
              <i class="fas fa-chart-line"></i>
              Analytics
            </router-link>
            <button type="button" role="menuitem" class="wizard-header-dropdown-item" @click="goToAccountAndCloseDropdown">
              <i class="fas fa-credit-card"></i>
              Manage payments
            </button>
            <button type="button" role="menuitem" class="wizard-header-dropdown-item wizard-header-dropdown-item-danger" @click="logoutUser">
              <i class="fas fa-sign-out-alt"></i>
              Log out
            </button>
          </div>
        </div>
      </header>
      <div
        v-if="showWizardOnlyNoNav"
        class="wizard-progress-bar"
        role="progressbar"
        :aria-valuenow="wizardStepTotal ? wizardStepCurrent + 1 : 0"
        :aria-valuemin="1"
        :aria-valuemax="wizardStepTotal"
        aria-label="Wizard progress"
      >
        <div class="wizard-progress-bar-fill" :style="{ width: wizardProgressPercent + '%' }"></div>
      </div>
    </div>

    <!-- Desktop Sidebar (hidden when new user in wizard with no resume; no logo, no footer) -->
    <aside class="sidebar glass-panel desktop-only" v-show="!showWizardOnlyNoNav">
      <div class="sidebar-header sidebar-header-menu-only"></div>

      <div class="sidebar-section">
        <span class="section-label" v-show="!sidebarCollapsed">Menu</span>
        <nav class="sidebar-nav">
          <button
            v-for="item in navItems"
            :key="item.view"
            class="nav-item"
            :class="{ active: currentView === item.view }"
            @click="changeView(item.view)"
            :title="sidebarCollapsed ? item.label : ''"
          >
            <i :class="item.icon"></i>
            <span v-show="!sidebarCollapsed">{{ item.label }}</span>
          </button>
        </nav>
      </div>
    </aside>

    <!-- Mobile Bottom Tab Bar (hidden when new user in wizard with no resume) -->
    <nav class="mobile-tabbar" v-if="isMobile && !showWizardOnlyNoNav">
      <button
        v-for="item in navItems"
        :key="'tab-' + item.view"
        class="tab-item"
        :class="{ active: currentView === item.view }"
        @click="changeView(item.view)"
      >
        <i :class="item.icon"></i>
        <span>{{ item.shortLabel || item.label }}</span>
      </button>
      <button class="tab-item logout-tab" @click="logoutUser">
        <i class="fa fa-sign-out-alt"></i>
        <span>Out</span>
      </button>
    </nav>

    <!-- Main Content (full width when wizard-only no navbar) -->
    <main class="main-content" :class="{ 'mobile-content': isMobile, 'content-is-wizard': currentView === 'SettingsComponent', 'full-width': showWizardOnlyNoNav, 'compact-wizard': currentView === 'SettingsComponent' && !showWizardOnlyNoNav }">
      <!-- Jobs View -->
      <div v-if="currentView === 'JobsComponent'" class="content-area">
        <div class="jobs-panel" style="position: relative;">
          <!-- Onboarding Overlay -->
          <div v-if="!isOnboardingComplete && !loadingJobs && !checkingResume" class="onboarding-overlay">
            <div class="onboarding-overlay-content">
              <div class="onboarding-icon">
                <i class="fa fa-file-alt"></i>
              </div>
              <h3>Complete Your Profile</h3>
              <p>Upload your resume and fill in your profile details to start generating tailored resumes.</p>
              <ul class="missing-items">
                <li v-for="field in missingProfileFields" :key="field">
                  <i class="fa fa-times-circle"></i> {{ field }}
                </li>
              </ul>
              <button @click="goToMyResume" class="btn btn-primary btn-lg">
                <i class="fa fa-arrow-right" style="margin-right: 8px;"></i>
                Complete My Resume
              </button>
            </div>
          </div>
          <!-- Skeleton Loading -->
          <div v-if="loadingJobs || checkingResume" class="jobs-skeleton">
            <div class="skeleton-card" v-for="n in 6" :key="n">
              <div class="skeleton-header">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-info">
                  <div class="skeleton-line skeleton-title"></div>
                  <div class="skeleton-line skeleton-subtitle"></div>
                </div>
              </div>
              <div class="skeleton-body">
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
              </div>
              <div class="skeleton-footer">
                <div class="skeleton-badge"></div>
                <div class="skeleton-badge"></div>
              </div>
            </div>
          </div>
          <!-- Actual Content -->
          <JobsGrid
            v-else
            :jobs="jobs"
            :jobs-total="jobsTotal"
            :jobs-page="jobsPage"
            :jobs-limit="jobsLimit"
            :loading-jobs="loadingJobs"
            @refresh-jobs="fetchJobs"
            @jobs-query="onJobsQuery"
            @update-job-status="onJobStatusUpdated"
            @update-job-notes="onJobNotesUpdated"
            @update-job-resume-generated="onJobResumeGenerated"
            @refresh-credits="fetchUserData"
            :disabled="!isOnboardingComplete"
          />
        </div>
      </div>

      <!-- Pipeline View — same section spacing as Account "Get More Credits" -->
      <div v-else-if="currentView === 'PipelineComponent'" class="content-area content-area-pipeline">
        <div class="pipeline-container">
          <div class="page-section">
            <h2>Your Pipeline</h2>
            <p class="page-section-subtitle">
              See where your job applications stand across each stage
            </p>
            <div class="pipeline-stats">
              <div class="stat-item glass-card">
                <span class="stat-value">{{ pipelineTotal }}</span>
                <span class="stat-label">Jobs</span>
              </div>
              <div class="stat-item glass-card">
                <span class="stat-value">{{ appliedCount }}</span>
                <span class="stat-label">In progress</span>
              </div>
            </div>
          </div>
          <div class="pipeline-content-panel">
            <div v-if="loadingPipelineStats || checkingResume" class="jobs-skeleton">
              <div class="skeleton-card" v-for="n in 3" :key="'pipe-' + n">
                <div class="skeleton-line skeleton-title"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>
            <ApplicationPipeline v-else :buckets="pipelineBuckets" :total="pipelineTotal" />
          </div>
        </div>
      </div>

      <!-- My Resume View: full-page wizard, no card/header -->
      <div v-else-if="currentView === 'SettingsComponent'" class="content-area content-area-wizard">
        <SettingsPanel
          :has-base-resume="hasBaseResume"
          @resume-uploaded="onResumeUploaded"
          @profile-updated="onProfileUpdated"
          @wizard-step-change="onWizardStepChange"
        />
      </div>

      <!-- Account View (no title/subtitle – Get More Credits only) -->
      <div v-else-if="currentView === 'AccountComponent'" class="content-area content-area-account">
        <AccountPanel :credits="userCredits" @refresh-credits="fetchUserData" />
      </div>

      <!-- Tutorial View: large title lives inside Tutorial (Apple grouped style) -->
      <div v-else-if="currentView === 'TutorialComponent'" class="content-area content-area-tutorial">
        <TutorialPanel @navigate="changeView" />
      </div>
    </main>

    <!-- Profile Incomplete Modal (not shown when new user in wizard with no resume) -->
    <Teleport to="body">
      <div v-if="showProfileIncompleteModal && !showWizardOnlyNoNav" class="modal-overlay" @click.self="showProfileIncompleteModal = false">
        <div class="modal-content profile-modal">
          <div class="modal-icon">
            <i class="fas fa-user-edit"></i>
          </div>
          <h2>Complete Your Profile</h2>
          <p>Please complete your profile before accessing this feature. We need this information to generate tailored resumes for you.</p>
          
          <div class="missing-fields">
            <h4>Missing Information:</h4>
            <ul>
              <li v-for="field in missingProfileFields" :key="field">
                <i class="fas fa-exclamation-circle"></i>
                {{ field }}
              </li>
            </ul>
          </div>

          <div class="modal-actions">
            <button class="btn-primary" @click="goToMyResume">
              <i class="fas fa-arrow-right"></i>
              Go to My Resume
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from "@/axios";
import JobsGrid from "@/components/JobsGrid.vue";
import ApplicationPipeline from "@/components/ApplicationPipeline.vue";
import SettingsPanel from "@/components/Settings.vue";
import AccountPanel from "@/components/Account.vue";
import TutorialPanel from "@/components/Tutorial.vue";
import AppLogo from "@/components/AppLogo.vue";
import { inject } from "vue";
import { mapActions, mapGetters } from "vuex";
import { subscribeRealtime } from "@/services/realtime";

export default {
  name: "DashboardComponent",
  components: {
    JobsGrid,
    ApplicationPipeline,
    SettingsPanel,
    AccountPanel,
    TutorialPanel,
    AppLogo,
  },
  data() {
    return {
      currentView: "JobsComponent",
      navItems: [
        { label: "Jobs", shortLabel: "Jobs", icon: "fas fa-briefcase", view: "JobsComponent" },
        { label: "Pipeline", shortLabel: "Pipeline", icon: "fas fa-chart-line", view: "PipelineComponent" },
        { label: "My Resume", shortLabel: "Resume", icon: "fas fa-file-alt", view: "SettingsComponent" },
        { label: "Account", shortLabel: "Account", icon: "fas fa-user", view: "AccountComponent" },
        { label: "Tutorial", shortLabel: "Help", icon: "fas fa-graduation-cap", view: "TutorialComponent" },
      ],
      jobs: [],
      jobsTotal: 0,
      jobsPage: 1,
      jobsLimit: 50,
      jobsSearch: "",
      jobsSortBy: "date",
      jobsSortDir: "desc",
      loadingJobs: true,
      pipelineBuckets: {
        saved: 0,
        ready: 0,
        applied: 0,
        interviewing: 0,
        offer: 0,
        passed: 0,
        skipped: 0,
      },
      pipelineTotal: 0,
      loadingPipelineStats: false,
      isSidebarOpen: true,
      sidebarCollapsed: false,
      headerDropdownOpen: false,
      wizardStepCurrent: 0,
      wizardStepTotal: 8,
      userCredits: 0,
      userSkills: [],
      showProfileIncompleteModal: false,
      hasBaseResume: false,
      checkingResume: true,
      jobsPollInterval: null,
      realtimeUnsubscribe: null,
    };
  },
  computed: {
    ...mapGetters("auth", ["user", "isAdmin"]),
    /** True when user is on wizard view and has never uploaded a resume → hide navbar and profile modal */
    showWizardOnlyNoNav() {
      return this.currentView === 'SettingsComponent' && !this.hasBaseResume;
    },
    userName() {
      return this.user?.firstName || this.user?.email?.split('@')[0] || 'User';
    },
    userDisplayName() {
      const first = this.user?.firstName?.trim();
      const last = this.user?.lastName?.trim();
      if (first && last) return `${first} ${last}`;
      if (first) return first;
      if (last) return last;
      return this.user?.email?.split('@')[0] || 'User';
    },
    userInitials() {
      const name = this.userDisplayName;
      return name.substring(0, 2).toUpperCase();
    },
    appliedCount() {
      const b = this.pipelineBuckets;
      return (b.applied || 0) + (b.interviewing || 0) + (b.offer || 0) + (b.passed || 0);
    },
    isProfileComplete() {
      const hasFirstName = !!this.user?.firstName?.trim();
      const hasLastName = !!this.user?.lastName?.trim();
      const hasEmail = !!this.user?.email?.trim();
      return hasFirstName && hasLastName && hasEmail;
    },
    isOnboardingComplete() {
      // User must have complete profile AND uploaded a base resume
      return this.isProfileComplete && this.hasBaseResume;
    },
    missingProfileFields() {
      const missing = [];
      if (!this.user?.firstName?.trim()) missing.push('First Name');
      if (!this.user?.lastName?.trim()) missing.push('Last Name');
      if (!this.user?.email?.trim()) missing.push('Email');
      if (!this.hasBaseResume) missing.push('Base Resume');
      return missing;
    },
    wizardProgressPercent() {
      if (!this.wizardStepTotal) return 0;
      return Math.round(((this.wizardStepCurrent + 1) / this.wizardStepTotal) * 100);
    },
    /** CSS vars for fixed wizard footer (align with main content; used when currentView === SettingsComponent) */
    wizardFooterStyle() {
      if (this.currentView !== 'SettingsComponent') return {};
      const mobile = this.isMobile;
      const left =
        mobile || this.showWizardOnlyNoNav
          ? '0'
          : this.sidebarCollapsed
            ? '72px'
            : '260px';
      /* Match --mobile-tabbar-height (content row + safe area) */
      const tabBarOffset =
        mobile && !this.showWizardOnlyNoNav
          ? 'var(--mobile-tabbar-height)'
          : '0px';
      // Mobile footer: single row Back + primary
      const footerHeight = mobile ? '64px' : '72px';
      return {
        '--wizard-footer-left': left,
        '--wizard-footer-height': footerHeight,
        '--wizard-footer-bottom': tabBarOffset,
        '--wizard-safe-bottom': '0px',
      };
    },
  },
  methods: {
    ...mapActions("auth", ["logout"]),
    onJobStatusUpdated({ jobId, status }) {
      const job = this.jobs.find((j) => j._id === jobId);
      if (job) job.status = status;
      this.fetchPipelineStats();
    },
    onJobNotesUpdated({ jobId, notes }) {
      const job = this.jobs.find((j) => j._id === jobId);
      if (job) job.notes = notes;
    },
    onJobResumeGenerated({ jobId, compatibilityScore }) {
      const job = this.jobs.find((j) => j._id === jobId);
      if (job) {
        job.hasResume = true;
        if (compatibilityScore) {
          job.compatibilityScore = compatibilityScore;
        }
      }
    },
    onJobsQuery({ page, search, sortBy, sortDir } = {}) {
      if (page != null) this.jobsPage = page;
      if (search !== undefined) {
        this.jobsSearch = search;
        this.jobsPage = 1;
      }
      if (sortBy) this.jobsSortBy = sortBy;
      if (sortDir) this.jobsSortDir = sortDir;
      this.fetchJobs();
    },
    async fetchJobs() {
      this.loadingJobs = true;
      try {
        const response = await axios.get("/api/jobs", {
          params: {
            page: this.jobsPage,
            limit: this.jobsLimit,
            search: this.jobsSearch || undefined,
            sortBy: this.jobsSortBy,
            sortDir: this.jobsSortDir,
          },
        });
        const data = response.data;
        if (Array.isArray(data)) {
          this.jobs = data;
          this.jobsTotal = data.length;
        } else {
          this.jobs = data.jobs || [];
          this.jobsTotal = data.total ?? this.jobs.length;
          this.jobsPage = data.page ?? this.jobsPage;
          this.jobsLimit = data.limit ?? this.jobsLimit;
        }
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        this.loadingJobs = false;
      }
    },
    async fetchPipelineStats() {
      this.loadingPipelineStats = true;
      try {
        const response = await axios.get("/api/jobs/stats");
        this.pipelineTotal = response.data.total || 0;
        this.pipelineBuckets = {
          saved: 0,
          ready: 0,
          applied: 0,
          interviewing: 0,
          offer: 0,
          passed: 0,
          skipped: 0,
          ...(response.data.buckets || {}),
        };
      } catch (error) {
        console.error("Error fetching pipeline stats:", error);
      } finally {
        this.loadingPipelineStats = false;
      }
    },
    async fetchUserData() {
      try {
        const response = await axios.get("/api/auth/me");
        const u = response.data.user;
        this.userCredits = u?.credits || 0;
        this.userSkills = u?.skills || [];
        if (u) {
          this.$store.commit("auth/PATCH_USER", {
            credits: u.credits,
            skills: u.skills,
            role: u.role,
            isAdmin: u.isAdmin,
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    },
    async checkBaseResume() {
      this.checkingResume = true;
      try {
        const response = await axios.get("/api/resume/preview");
        this.hasBaseResume = response.data.hasResume === true;
      } catch (error) {
        this.hasBaseResume = false;
      } finally {
        this.checkingResume = false;
      }
    },
    async onResumeUploaded() {
      // Refresh the resume check when user uploads a resume
      await this.checkBaseResume();
      // If now complete, can switch to Jobs view
      if (this.isOnboardingComplete) {
        this.showProfileIncompleteModal = false;
      }
    },
    async onProfileUpdated() {
      // Refresh user data when profile is updated
      await this.fetchUserData();
      // If now complete, can switch to Jobs view
      if (this.isOnboardingComplete) {
        this.showProfileIncompleteModal = false;
      }
    },
    checkProfileComplete() {
      if (!this.isProfileComplete) {
        this.showProfileIncompleteModal = true;
        return false;
      }
      return true;
    },
    goToMyResume() {
      this.showProfileIncompleteModal = false;
      this.currentView = 'SettingsComponent';
    },
    changeView(view) {
      // Allow access to My Resume and Tutorial without onboarding check
      // Block Jobs and Account until onboarding is complete (profile + resume)
      if ((view === 'JobsComponent' || view === 'PipelineComponent' || view === 'AccountComponent') && !this.isOnboardingComplete) {
        this.showProfileIncompleteModal = true;
        return;
      }
      this.currentView = view;
      if (view === "PipelineComponent") {
        this.fetchPipelineStats();
      }
      if (this.isMobile) {
        this.isSidebarOpen = false;
      }
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    toggleSidebarCollapse() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      // Save preference
      localStorage.setItem('sidebarCollapsed', this.sidebarCollapsed);
    },
    startJobsPolling() {
      // Poll for job updates every 30 seconds when on Jobs view
      this.jobsPollInterval = setInterval(() => {
        if (this.currentView === 'JobsComponent' && !this.loadingJobs) {
          this.fetchJobsSilent();
        }
      }, 30000);
    },
    stopJobsPolling() {
      if (this.jobsPollInterval) {
        clearInterval(this.jobsPollInterval);
        this.jobsPollInterval = null;
      }
    },
    async fetchJobsSilent() {
      try {
        const response = await axios.get("/api/jobs", {
          params: {
            page: this.jobsPage,
            limit: this.jobsLimit,
            search: this.jobsSearch || undefined,
            sortBy: this.jobsSortBy,
            sortDir: this.jobsSortDir,
          },
        });
        const data = response.data;
        if (Array.isArray(data)) {
          this.jobs = data;
          this.jobsTotal = data.length;
        } else {
          this.jobs = data.jobs || [];
          this.jobsTotal = data.total ?? this.jobs.length;
        }
      } catch (error) {
        // Silent fail
      }
    },
    handleResize() {
      if (window.innerWidth > 768) {
        this.isSidebarOpen = true;
      }
    },
    async logoutUser() {
      this.headerDropdownOpen = false;
      await this.logout();
      this.$router.push("/login");
    },
    goToAccountAndCloseDropdown() {
      this.headerDropdownOpen = false;
      this.changeView('AccountComponent');
    },
    onHeaderClickOutside(e) {
      if (this.headerDropdownOpen && this.$refs.headerUserCardWrap && !this.$refs.headerUserCardWrap.contains(e.target)) {
        this.headerDropdownOpen = false;
      }
    },
    onWizardStepChange({ currentStepIndex, totalSteps }) {
      this.wizardStepCurrent = currentStepIndex;
      this.wizardStepTotal = totalSteps || 8;
    },
  },
  async mounted() {
    await Promise.all([
      this.fetchUserData(),
      this.checkBaseResume()
    ]);
    this.fetchJobs();
    this.fetchPipelineStats();
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
    
    // Restore sidebar collapsed state
    const savedCollapsed = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsed === 'true') {
      this.sidebarCollapsed = true;
    }
    
    // Start polling for job updates
    this.startJobsPolling();

    // Realtime sync (extension / other tabs) — refetch when job or credits change
    this.realtimeUnsubscribe = subscribeRealtime(
      () => localStorage.getItem("token"),
      {
        onJobs: () => {
          this.fetchJobs();
          this.fetchPipelineStats();
        },
        onCredits: () => {
          this.fetchUserData();
          this.fetchJobs();
        },
      }
    );

    document.addEventListener('click', this.onHeaderClickOutside);

    // Check for view query parameter (e.g., from Stripe redirect)
    const urlParams = new URLSearchParams(window.location.search);
    const view = urlParams.get('view');
    if (view === 'account') {
      this.currentView = 'AccountComponent';
    } else if (view === 'settings') {
      this.currentView = 'SettingsComponent';
    }
    
    // If onboarding incomplete, redirect to My Resume; only show Complete Profile modal if they already have a resume
    if (!this.isOnboardingComplete) {
      this.currentView = 'SettingsComponent';
      this.showProfileIncompleteModal = this.hasBaseResume && !this.isProfileComplete;
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener('click', this.onHeaderClickOutside);
    this.stopJobsPolling();
    if (this.realtimeUnsubscribe) this.realtimeUnsubscribe();
  },
  setup() {
    const isMobile = inject("isMobile");
    return { isMobile };
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

.dashboard {
  display: flex;
  min-height: 100vh;
  background: var(--app-bg);
  position: relative;
  overflow: hidden;
}

/* Animated Gradient Background – theme-aware */
.bg-gradient {
  position: fixed;
  inset: 0;
  background: var(--app-bg);
  z-index: 0;
}

.bg-blob {
  position: fixed;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  pointer-events: none;
  z-index: 0;
}

.blob-1 {
  width: 600px;
  height: 600px;
  background: var(--blob-1);
  top: -200px;
  right: -100px;
  opacity: 0.3;
}

.blob-2 {
  width: 400px;
  height: 400px;
  background: var(--blob-2);
  bottom: -100px;
  left: -100px;
  opacity: 0.2;
}

.blob-3 {
  width: 300px;
  height: 300px;
  background: var(--blob-3);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.15;
}

/* Onboarding Overlay */
.onboarding-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--surface-2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-radius: 16px;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.onboarding-overlay-content {
  text-align: center;
  max-width: 400px;
}

.onboarding-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--step-active-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  border: 2px solid var(--primary);
}

.onboarding-icon i {
  font-size: 32px;
  color: var(--primary);
}

.onboarding-overlay-content h3 {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.onboarding-overlay-content p {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 20px;
}

.missing-items {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
  text-align: left;
  display: inline-block;
}

.missing-items li {
  color: var(--text-secondary);
  font-size: 13px;
  padding: 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.missing-items li i {
  color: var(--danger);
  font-size: 12px;
}

.btn-lg {
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
}

/* Glassmorphism Base Classes */
.glass-panel {
  background: var(--app-surface);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid var(--app-border);
  border-radius: 16px;
}

.glass-card {
  background: var(--app-surface);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--app-border);
  border-radius: 12px;
}

/* Mobile Header – theme-aware */
.mobile-header {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: var(--app-bg-secondary);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--app-border);
  padding: 0 20px;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
}

.menu-toggle {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  color: var(--app-text);
  font-size: 20px;
  cursor: pointer;
  padding: 10px 12px;
  border-radius: 10px;
  transition: all 0.2s;
}

.menu-toggle:hover {
  background: var(--app-surface);
}

/* Sidebar – theme-aware. Borderless when wizard (all-white flow) is shown */
.sidebar {
  width: 260px;
  background: var(--app-bg-secondary);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border-right: 1px solid var(--app-border);
  border-radius: 0;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 50;
  transition: width 0.3s ease;
}

.dashboard:has(.main-content.content-is-wizard) .sidebar {
  border-right: none;
}

.sidebar.collapsed {
  width: 72px;
}

.sidebar.collapsed .section-label,
.sidebar.collapsed .user-card,
.sidebar.collapsed .user-details,
.sidebar.collapsed .nav-item span,
.sidebar.collapsed .logout-btn span {
  display: none !important;
}

.sidebar.collapsed .nav-item {
  justify-content: center;
  padding: 14px;
}

.sidebar.collapsed .nav-item i {
  margin: 0;
}

.sidebar.collapsed .logout-btn {
  justify-content: center;
  padding: 12px;
}

.sidebar.collapsed .sidebar-header {
  position: relative;
  justify-content: center;
  padding: 20px 12px;
}

.sidebar.collapsed .sidebar-header .logo {
  justify-content: center;
}

.sidebar-header {
  padding: 24px 20px;
  border-bottom: 1px solid var(--app-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sidebar-header-menu-only {
  min-height: 16px;
  padding: 16px 20px;
}

.sidebar-header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.theme-toggle,
.sidebar-toggle {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 8px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.theme-toggle:hover,
.sidebar-toggle:hover {
  background: var(--app-surface);
  color: var(--app-text);
}

.sidebar.collapsed .sidebar-header-actions {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  flex-direction: column;
  gap: 4px;
}

.sidebar.collapsed .theme-toggle,
.sidebar.collapsed .sidebar-toggle {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
}

.user-avatar-only {
  width: 42px;
  height: 42px;
  background: var(--primary);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-foreground);
  font-weight: 600;
  font-size: 14px;
  margin: 0 auto 12px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.logo-img {
  height: 24px;
}

.logo.logo-full-width {
  width: 100%;
  justify-content: center;
}

.logo.logo-full-width .logo-img {
  width: 100%;
  height: auto;
  max-height: 24px;
  object-fit: contain;
}


.sidebar-section {
  flex: 1;
  padding: 20px 16px;
}

.section-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--app-text-secondary);
  margin-bottom: 12px;
  padding-left: 12px;
}

.sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--app-text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  width: 100%;
  text-align: left;
  box-shadow: none;
}

.nav-item i {
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.nav-item:hover {
  background: var(--app-surface-hover);
  color: var(--app-text);
}

.nav-item.active {
  background: color-mix(in srgb, var(--app-primary) 10%, var(--app-surface));
  color: var(--app-primary);
  border: none;
  box-shadow: none;
}

.sidebar-footer {
  padding: 16px;
  border-top: 1px solid var(--app-border);
}

.user-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 12px;
}

.user-avatar {
  width: 42px;
  height: 42px;
  background: var(--primary);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-foreground);
  font-weight: 600;
  font-size: 14px;
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-name {
  color: var(--app-text);
  font-weight: 500;
  font-size: 14px;
}

.user-credits {
  color: var(--app-primary);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.user-credits i {
  font-size: 10px;
}

.logout-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  color: var(--app-text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.logout-btn:hover {
  background: var(--danger-subtle);
  border-color: var(--danger);
  color: var(--danger);
}

.sidebar-overlay {
  display: none;
}

/* Main Content — horizontal inset matches .app-header via --dashboard-inline-pad */
.main-content {
  flex: 1;
  margin-left: 260px;
  padding: 32px var(--dashboard-inline-pad, 24px);
  position: relative;
  z-index: 1;
  transition: margin-left 0.3s ease;
}

/* Non-wizard: fixed height below header, no scroll on main content – only inner area scrolls */
.main-content:not(.content-is-wizard) {
  height: calc(100vh - var(--app-header-height, 64px));
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dashboard.sidebar-collapsed .main-content {
  margin-left: 72px;
}

.dashboard {
  --app-header-height: 64px;
  /* Content row (~56px) + home indicator — must match .mobile-tabbar box height */
  --mobile-tabbar-height: calc(56px + env(safe-area-inset-bottom, 0px));
  --dashboard-inline-pad: 24px;
}

/* Shared iOS large title — same scroll column / leading edge as cards */
.ios-large-title {
  width: 100%;
  padding: 0 0 12px;
  text-align: left;
}

.ios-large-title h1 {
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.35px;
  line-height: 1.2;
  color: var(--app-text);
}

.ios-large-title p {
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.35;
  color: var(--text-secondary);
}

.ios-large-title .ios-title-meta {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
}

.page-header-grouped {
  display: block;
  margin-bottom: 8px;
  flex-shrink: 0;
}

/* Pipeline — same scroll shell as Account; title uses shared .page-section */
.pipeline-container {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
}

.pipeline-stats {
  display: flex;
  width: 100%;
  gap: 8px;
  align-items: stretch;
}

.pipeline-stats .stat-item {
  flex: 1 1 0;
  width: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 10px 12px;
  border-radius: var(--card-radius, 12px);
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  box-shadow: var(--shadow-card);
  text-align: center;
}

.pipeline-stats .stat-item.glass-card {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  box-shadow: var(--shadow-card);
  backdrop-filter: none;
}

.pipeline-stats .stat-value {
  font-size: 17px;
  font-weight: 700;
  color: var(--app-text);
  margin: 0;
  line-height: 1.1;
}

.pipeline-stats .stat-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--app-text-secondary);
  line-height: 1.1;
}

.dashboard.wizard-only-no-nav {
  --app-header-height: 67px;
}

/* Push main content block below fixed header so it never sits under it */
.dashboard .main-content {
  margin-top: var(--app-header-height, 64px);
  padding-top: 4px;
  padding-left: var(--dashboard-inline-pad, 24px);
  padding-right: var(--dashboard-inline-pad, 24px);
  box-sizing: border-box;
}

/* Don't add top margin when wizard: wizard uses its own layout below header */
.dashboard .main-content.content-is-wizard {
  margin-top: 0;
  padding: 0;
}

.dashboard.wizard-only-no-nav .main-content {
  margin-left: 0;
  max-width: none;
}

/* App header (throughout the app): logo left, user-card right */
.app-header-wrap {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--app-surface);
  border-bottom: 1px solid var(--app-border);
  height: var(--app-header-height, 64px);
}

.app-header-wrap.with-progress {
  height: 67px;
}

.app-header {
  height: 64px;
  padding: 0 var(--dashboard-inline-pad, 24px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.app-header-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.app-header-logo-img {
  height: 24px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

.app-header-right {
  position: relative;
  display: flex;
  align-items: center;
}

.app-header-user-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  background: var(--surface-2, #ffffff);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s;
  font: inherit;
  color: inherit;
  text-align: left;
  width: auto;
  box-shadow: none !important;
  filter: none !important;
  -webkit-tap-highlight-color: transparent;
}

.app-header-user-card:hover,
.app-header-user-card:focus,
.app-header-user-card:active {
  background: var(--surface-3, #f2f2f7);
  box-shadow: none !important;
  filter: none !important;
}

.app-header-user-card .user-avatar {
  display: none;
}

.app-header-user-card .user-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
}

.app-header-user-card .user-name {
  color: var(--app-text, #1a1a2e);
  font-weight: 500;
  font-size: 13px;
  line-height: 1.2;
}

.app-header-user-card .user-credits {
  color: var(--app-primary, #5b21b6);
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  line-height: 1.2;
  white-space: nowrap;
}

.app-header-dropdown {
  top: calc(100% + 8px);
  right: 0;
}

.wizard-progress-bar {
  height: 3px;
  width: 100%;
  background: var(--surface-3, #e5e5ea);
  overflow: hidden;
}

.wizard-progress-bar-fill {
  height: 100%;
  background: var(--primary, #5b21b6);
  transition: width 0.25s ease;
}

.wizard-header-logo {
  display: flex;
  align-items: center;
  text-decoration: none;
}

.wizard-header-logo-img {
  height: 24px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
}

.wizard-header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.wizard-header-credits {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--app-primary, #5b21b6);
}

.wizard-header-credits i {
  font-size: 12px;
}

.wizard-header-avatar-wrap {
  position: relative;
}

.wizard-header-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--primary, #5b21b6);
  color: var(--primary-foreground, #fff);
  font-weight: 600;
  font-size: 14px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.wizard-header-avatar:hover {
  filter: brightness(1.1);
}

.wizard-header-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  box-shadow: var(--shadow-modal);
  padding: 6px 0;
  z-index: 200;
}

.wizard-header-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: inherit;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  color: var(--app-text, #1a1a2e);
  transition: background 0.2s;
}

.wizard-header-dropdown-item:hover {
  background: var(--app-surface-hover);
}

.wizard-header-dropdown-item i {
  width: 18px;
  text-align: center;
  color: var(--app-text-secondary, #64748b);
}

.wizard-header-dropdown-item-danger {
  color: var(--danger, #dc2626);
}

.wizard-header-dropdown-item-danger:hover {
  background: var(--danger-subtle, #fef2f2);
}

.wizard-header-dropdown-item-danger i {
  color: inherit;
}

.content-area {
  max-width: 100%;
  margin: 0;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.main-content:not(.content-is-wizard) .content-area .page-header {
  flex-shrink: 0;
  min-width: 0;
}

/* Jobs/Account/Tutorial: content below page header fills space; scroll lives inside (table or panel content) */
.main-content:not(.content-is-wizard) .content-area .content-panel,
.main-content:not(.content-is-wizard) .content-area > *:not(.page-header) {
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Panel's single child (jobs-skeleton, JobsGrid root, or Account/Tutorial panel) fills height */
.main-content:not(.content-is-wizard) .content-area .content-panel > *,
.main-content:not(.content-is-wizard) .content-area > *:not(.page-header) > * {
  flex: 1;
  min-height: 0;
  min-width: 0;
}

/* Help: only the scroll body grows — section tabs stay compact */
.main-content:not(.content-is-wizard) .content-area-tutorial > * > .section-tabs {
  flex: 0 0 auto;
  min-height: 0;
  max-height: none;
}

.main-content:not(.content-is-wizard) .content-area-tutorial > * > .tutorial-scroll {
  flex: 1 1 auto;
  min-height: 0;
}

/* Account: content sizes naturally — no stretched empty band above credits */
.main-content:not(.content-is-wizard) .content-area-account > * > .account-customize {
  flex: 0 0 auto;
  min-height: 0;
}

/* Pipeline: same scroll model as Account */
.main-content:not(.content-is-wizard) .content-area-pipeline > .pipeline-container {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  display: block;
}

.main-content:not(.content-is-wizard) .content-area-pipeline > .pipeline-container > * {
  flex: none;
  min-height: auto;
}

/* Account/Tutorial: panel content scrolls (no inner table wrapper) */
.main-content:not(.content-is-wizard) .content-area > *:not(.page-header):not(.content-panel) {
  overflow: auto;
}

.pipeline-content-panel {
  padding: 16px;
  overflow: visible;
  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: var(--card-radius, 12px);
  box-shadow: none;
  margin: 0 4px 24px;
}

.pipeline-content-panel > * {
  flex: none !important;
  min-height: auto !important;
}

/* Wizard: main content sits below sticky header, fills viewport; wizard scrolls inside (no padding hack) */
.main-content.content-is-wizard {
  position: fixed;
  top: var(--app-header-height, 64px);
  left: 260px;
  right: 0;
  bottom: 0;
  height: auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0;
  background: var(--app-bg);
}

.dashboard.sidebar-collapsed .main-content.content-is-wizard {
  left: 72px;
}

.dashboard.wizard-only-no-nav .main-content.content-is-wizard {
  left: 0;
}

/* Existing user (sidebar visible): flow normally; no scroll on main so wizard scrolls inside and footer stays sticky */
.main-content.content-is-wizard.compact-wizard {
  position: static;
  left: auto;
  right: auto;
  top: auto;
  bottom: auto;
  padding-top: var(--app-header-height, 64px);
  overflow: hidden;
}

.content-area-wizard {
  max-width: none;
  margin: 0;
  padding: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* Wizard wrapper (e.g. Settings root) must fill height so only the form scrolls, not the footer */
.main-content.content-is-wizard .content-area-wizard > * {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Existing user (sidebar visible): reduce horizontal padding so wizard uses more width */
.main-content.compact-wizard .content-area-wizard :deep(.wizard-form) {
  padding-left: 20px;
  padding-right: 20px;
}
.main-content.compact-wizard .content-area-wizard :deep(.wizard-footer) {
  padding-left: 20px;
  padding-right: 20px;
}
.main-content.compact-wizard .content-area-wizard :deep(.wizard-stepper) {
  padding-left: 20px;
  padding-right: 20px;
}

/* Wizard stepper: ensure labels inherit theme text (fixes light-bg invisible labels) */
.main-content.content-is-wizard :deep(.wizard-stepper-item),
.main-content.content-is-wizard :deep(.wizard-stepper-label),
.main-content.content-is-wizard :deep(.wizard-step-title),
.main-content.content-is-wizard :deep(.wizard-step-helper) {
  color: var(--app-text);
}

.main-content.content-is-wizard :deep(.wizard-stepper-item.is-pending) {
  color: var(--text-muted);
}

.main-content.content-is-wizard :deep(.wizard-stepper-progress),
.main-content.content-is-wizard :deep(.wizard-stepper-helper) {
  color: var(--text-muted);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 28px;
}

/* Tutorial (and any other compact header): same title/subtitle sizes as Jobs */
.page-header-compact {
  align-items: center;
  margin-bottom: 12px;
}

.page-header-compact .header-text h1 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 2px;
}

.page-header-compact .header-text p {
  font-size: 12px;
}

.header-text h1 {
  font-size: 28px;
  font-weight: 700;
  color: var(--app-text);
  margin-bottom: 6px;
}

.header-text p {
  color: var(--app-text-secondary);
  font-size: 14px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-refresh {
  background: var(--app-surface-hover);
  border: 1px solid var(--app-border);
  border-radius: 10px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--app-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-refresh:hover:not(:disabled) {
  background: var(--app-surface);
  color: var(--app-text);
  border-color: var(--app-border);
}

.btn-refresh:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.header-stats {
  display: flex;
  gap: 12px;
}

.stat-item {
  padding: 16px 24px;
  text-align: center;
  min-width: 100px;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--app-text);
  margin-bottom: 2px;
}

.stat-label {
  font-size: 12px;
  color: var(--app-text-secondary);
}

.content-panel {
  padding: 24px;
}

/* Jobs: no outer glass box — cards/table sit on the page background */
.jobs-panel {
  position: relative;
  flex: 1;
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
}

.settings-panel {
  overflow: visible;
}

/* Desktop only class */
.desktop-only {
  display: flex;
}

/* Mobile Tab Bar */
.mobile-tabbar {
  display: none;
}

/* Mobile User Info in Header */
.mobile-user-info {
  display: flex;
  align-items: center;
}

.mobile-credits {
  background: var(--app-surface-hover);
  color: var(--app-primary);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.mobile-credits i {
  font-size: 11px;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard {
    --dashboard-inline-pad: 16px;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-header {
    display: flex;
  }

  .mobile-tabbar {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: var(--surface-2);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: 1px solid var(--divider);
    padding-top: 6px;
    padding-left: calc(12px + env(safe-area-inset-left, 0px));
    padding-right: calc(12px + env(safe-area-inset-right, 0px));
    /* Compact labels + home-indicator only — matches --mobile-tabbar-height */
    padding-bottom: calc(6px + env(safe-area-inset-bottom, 0px));
    z-index: 100;
    justify-content: space-between;
    align-items: center;
    gap: 0;
    box-sizing: border-box;
    min-height: var(--mobile-tabbar-height);
  }

  .tab-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    padding: 4px 2px;
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 10px;
    font-weight: 500;
    line-height: 1.1;
    cursor: pointer;
    border-radius: 10px;
    transition: color 0.2s, background 0.2s;
    min-width: 0;
    flex: 1 1 0;
    height: 44px;
    min-height: 44px;
    max-height: 44px;
    box-shadow: none !important;
    filter: none !important;
    -webkit-appearance: none;
    appearance: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  .tab-item i {
    font-size: 18px;
    line-height: 1;
  }

  .tab-item span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .tab-item.active {
    color: var(--primary);
    background: transparent;
    box-shadow: none !important;
    filter: none !important;
  }

  .tab-item.logout-tab {
    color: var(--danger);
    opacity: 0.9;
  }

  .tab-item.logout-tab:hover {
    color: var(--danger);
    background: var(--danger-subtle);
    box-shadow: none !important;
  }

  /* Height ends above the fixed tab bar (padding on dashboard is the reserved lane) */
  .dashboard.has-tabbar {
    height: 100dvh;
    max-height: 100dvh;
    padding-bottom: var(--mobile-tabbar-height);
    box-sizing: border-box;
    overflow: hidden;
  }

  .dashboard.has-tabbar .main-content:not(.content-is-wizard) {
    height: calc(100% - var(--app-header-height, 64px));
    max-height: calc(100% - var(--app-header-height, 64px));
    overflow: hidden;
  }

  .dashboard .main-content {
    margin-left: 0;
    padding-top: 8px;
    padding-left: var(--dashboard-inline-pad, 16px);
    padding-right: var(--dashboard-inline-pad, 16px);
    padding-bottom: 8px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  /* Same inset as main content — keeps logo/profile flush with toolbar/cards */
  .dashboard .app-header {
    padding-left: var(--dashboard-inline-pad, 16px);
    padding-right: var(--dashboard-inline-pad, 16px);
  }

  .dashboard .main-content.content-is-wizard {
    padding: 0;
    padding-top: var(--app-header-height, 64px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    height: calc(100% - var(--app-header-height, 64px));
    max-height: calc(100% - var(--app-header-height, 64px));
  }

  .dashboard:not(.wizard-only-no-nav) .main-content.content-is-wizard {
    height: calc(100% - var(--app-header-height, 64px));
    max-height: calc(100% - var(--app-header-height, 64px));
  }

  .main-content.content-is-wizard.compact-wizard {
    position: static;
    left: auto;
    right: auto;
  }

  .main-content.mobile-content {
    padding-bottom: 8px;
    overflow: hidden;
  }

  .content-area {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
  }

  .page-header {
    flex-direction: column;
    gap: 12px;
  }

  .page-header-grouped {
    margin-bottom: 4px;
  }

  .ios-large-title {
    padding: 0 0 8px;
  }

  .ios-large-title h1 {
    font-size: 22px;
  }

  .ios-large-title p {
    font-size: 15px;
  }

  .ios-large-title .ios-title-meta {
    font-size: 13px;
  }

  /* Legacy compact headers (if any remain) */
  .page-header-compact {
    align-items: stretch;
    width: 100%;
    margin-bottom: 16px;
  }

  .page-header-compact .header-text {
    width: 100%;
    text-align: left;
  }

  .page-header-compact .header-text h1 {
    font-size: 28px;
    font-weight: 700;
    color: var(--app-text);
    margin: 0 0 6px 0;
  }

  .page-header-compact .header-text p {
    font-size: 15px;
    color: var(--text-secondary);
    opacity: 1;
    margin: 0;
    line-height: 1.35;
  }

  .header-stats {
    display: flex;
    width: auto;
    gap: 6px;
  }

  .stat-item {
    flex: 0 1 auto;
    min-width: 0;
    padding: 6px 10px;
    border-radius: var(--card-radius, 12px);
    background: var(--surface-2);
    border: 1px solid var(--border);
    box-shadow: var(--shadow-card);
  }

  .stat-value {
    display: block;
    font-size: 14px;
    font-weight: 700;
    color: var(--app-text);
    margin-bottom: 0;
  }

  .stat-label {
    display: block;
    font-size: 10px;
    color: var(--app-text-secondary);
  }

  .header-text h1 {
    font-size: 28px;
  }

  .header-text p {
    font-size: 15px;
  }

  .content-panel {
    padding: 12px;
    border-radius: var(--card-radius, 12px);
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
    box-shadow: var(--shadow-card);
  }

  .pipeline-content-panel {
    padding: 12px;
    padding-bottom: 24px;
    margin: 0 8px 24px;
  }

  /* Hide blobs on mobile for performance */
  .bg-blob {
    display: none;
  }
}

@media (max-width: 480px) {
  .dashboard {
    --dashboard-inline-pad: 12px;
  }

  .mobile-header {
    height: 54px;
    padding: 0 12px;
  }

  .logo-img {
    height: 24px;
  }


  .mobile-credits {
    padding: 5px 10px;
    font-size: 12px;
  }

  .dashboard .main-content {
    padding-left: var(--dashboard-inline-pad, 12px);
    padding-right: var(--dashboard-inline-pad, 12px);
  }

  .dashboard .app-header {
    padding-left: var(--dashboard-inline-pad, 12px);
    padding-right: var(--dashboard-inline-pad, 12px);
  }

  .header-text h1 {
    font-size: 18px;
  }

  .header-text p {
    font-size: 11px;
  }

  .stat-item {
    padding: 8px 10px;
  }

  .stat-value {
    font-size: 16px;
  }

  .content-panel {
    padding: 10px;
    border-radius: 10px;
  }
}

/* Skeleton Loading - fill panel height when loading so it's not too small */
.jobs-skeleton {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  align-content: start;
  min-height: 100%;
}

.skeleton-card {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 20px;
}

.skeleton-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.skeleton-avatar {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: linear-gradient(90deg, var(--surface-3) 25%, var(--border) 50%, var(--surface-3) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

.skeleton-info {
  flex: 1;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--surface-3) 25%, var(--border) 50%, var(--surface-3) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
  margin-bottom: 8px;
}

.skeleton-title {
  width: 70%;
  height: 14px;
}

.skeleton-subtitle {
  width: 50%;
  height: 10px;
}

.skeleton-body {
  margin-bottom: 16px;
}

.skeleton-body .skeleton-line {
  width: 100%;
}

.skeleton-body .skeleton-line.short {
  width: 60%;
}

.skeleton-footer {
  display: flex;
  gap: 10px;
}

.skeleton-badge {
  width: 70px;
  height: 24px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--surface-3) 25%, var(--border) 50%, var(--surface-3) 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Profile Incomplete Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--app-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content.profile-modal {
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 32px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: var(--shadow-modal);
}

.modal-icon {
  width: 72px;
  height: 72px;
  background: var(--primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.modal-icon i {
  font-size: 32px;
  color: var(--primary-foreground);
}

.profile-modal h2 {
  color: var(--text-primary);
  font-size: 22px;
  font-weight: 600;
  margin: 0 0 12px;
}

.profile-modal > p {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 24px;
}

.missing-fields {
  background: var(--danger-subtle);
  border: 1px solid var(--danger);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  text-align: left;
}

.missing-fields h4 {
  color: var(--danger);
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 12px;
}

.missing-fields ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.missing-fields li {
  color: var(--text-secondary);
  font-size: 13px;
  padding: 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.missing-fields li i {
  font-size: 12px;
  color: var(--danger);
}

.modal-actions {
  display: flex;
  justify-content: center;
}

.modal-actions .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 28px;
  background: var(--primary);
  border: none;
  border-radius: 10px;
  color: var(--primary-foreground);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-actions .btn-primary:hover {
  background: var(--primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 16px var(--focus-ring);
}

@media (max-width: 480px) {
  .modal-content.profile-modal {
    padding: 24px;
  }

  .modal-icon {
    width: 56px;
    height: 56px;
  }

  .modal-icon i {
    font-size: 24px;
  }

  .profile-modal h2 {
    font-size: 18px;
  }

  .profile-modal > p {
    font-size: 13px;
  }
}
</style>
